/**
 * Small inline chips that surface model and reasoning effort controls
 * inside the input footer.
 */

import type { LucideIcon } from 'lucide-react';
import {
  BatteryFull,
  BatteryLow,
  BatteryMedium,
  Brain,
  Check,
  ChevronDown,
  FileText,
  Flame,
  Gauge,
  ShieldCheck,
  Signal,
  Sparkles,
  TerminalSquare,
  Zap,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { AgentSpeed, PermissionMode } from '@/lib/agent/types';
import { cn } from '@/lib/utils/cn';

// -----------------------------------------------------------------------------
// Metadata pills (model, effort)
// -----------------------------------------------------------------------------

// Structure-only chip classes (size, padding, radius, type). Color
// comes from a separate token so callers can swap palettes without
// string-mutating the base.
function chipBase(): string {
  return 'inline-flex shrink-0 select-none items-center gap-1 rounded-full px-2.5 py-1 font-medium text-[11.5px] leading-none';
}

const CHIP_NEUTRAL = 'bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-400';
const PLAN_MODE_CHIP = 'bg-[#245f9f]/[0.07] text-[#245f9f] dark:bg-[#6fa3dc]/18 dark:text-[#bddcff]';
const PLAN_MODE_ICON = 'text-[#245f9f] opacity-100 dark:text-[#bddcff]';

function chipClass(): string {
  return cn(chipBase(), CHIP_NEUTRAL);
}

export interface ModelPillProps {
  model: string;
  className?: string;
  /**
   * Class applied to the text label wrapper. Pass a container-query
   * variant such as ``@max-[460px]/composer:hidden`` to collapse the
   * pill to its icon when the parent (e.g. the agent composer) gets
   * too narrow to fit the full label without colliding with the
   * send/stop buttons on the right. The icon + ``title`` tooltip
   * remain so the pill stays useful and accessible.
   */
  labelClassName?: string;
}

/** Stylized model name (e.g. "Opus 4.8") with a sparkle glyph. */
export function ModelPill({ model, className, labelClassName }: ModelPillProps) {
  return (
    <span className={cn(chipClass(), className)} title={model}>
      <Sparkles className="size-3 opacity-70" />
      <span className={labelClassName}>{prettyModel(model)}</span>
    </span>
  );
}

export interface EffortPillProps {
  effort: string;
  /**
   * If provided, the pill renders as a clickable trigger that opens
   * a small dropdown with the supported reasoning levels. Calling
   * the callback initiates a session reconnect at the new level
   * (see :func:`AgentPanelProvider.setEffort`).
   *
   * Omit to render a static, non-interactive label.
   */
  onChange?: (effort: string) => void;
  /** Disable interaction (e.g. while the WS is reconnecting). */
  disabled?: boolean;
  className?: string;
  /** See :attr:`ModelPillProps.labelClassName`. */
  labelClassName?: string;
}

/**
 * Reasoning effort level (e.g. "Extra High") with a brain glyph.
 *
 * Static label by default; becomes an interactive picker the moment
 * an :attr:`onChange` callback is provided.
 */
export function EffortPill({ effort, onChange, disabled = false, className, labelClassName }: EffortPillProps) {
  const ActiveIcon = effortIcon(effort);
  if (!onChange) {
    return (
      <span className={cn(chipClass(), className)} title={`Reasoning effort: ${prettyEffort(effort)}`}>
        <ActiveIcon className="size-3 opacity-70" />
        <span className={labelClassName}>{prettyEffort(effort)}</span>
      </span>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <button
          type="button"
          title={`Reasoning effort: ${prettyEffort(effort)}`}
          aria-label={`Reasoning effort: ${prettyEffort(effort)}. Click to change.`}
          className={cn(
            chipClass(),
            'cursor-pointer transition-colors duration-150',
            'hover:bg-gray-200 dark:hover:bg-white/10',
            'focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-300 dark:focus-visible:ring-white/20',
            disabled && 'cursor-not-allowed opacity-60',
            className
          )}
        >
          <ActiveIcon className="size-3 opacity-70" />
          <span className={labelClassName}>{prettyEffort(effort)}</span>
          <ChevronDown className="size-2.5 opacity-50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-44">
        <DropdownMenuLabel>
          <Brain />
          Reasoning effort
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {EFFORT_LEVELS.map((level) => {
          const isCurrent = level === effort;
          const LevelIcon = effortIcon(level);
          return (
            <DropdownMenuItem
              key={level}
              onSelect={() => {
                if (!isCurrent) onChange(level);
              }}
            >
              <LevelIcon />
              <span className="flex-1">{prettyEffort(level)}</span>
              <Check className={cn('size-3 shrink-0', isCurrent ? 'opacity-100' : 'opacity-0')} />
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface SpeedPillProps {
  speed?: AgentSpeed | string | null;
  onChange?: (speed: AgentSpeed) => void;
  disabled?: boolean;
  className?: string;
  /** See :attr:`ModelPillProps.labelClassName`. */
  labelClassName?: string;
}

/**
 * Claude Code speed picker.
 *
 * Renders ``null`` when the server hasn't reported a speed yet
 * (older workspace servers). ``Fast`` is an explicit session-level
 * opt-in because it is beta/limited-availability and can carry
 * premium pricing.
 */
export function SpeedPill({ speed, onChange, disabled = false, className, labelClassName }: SpeedPillProps) {
  const active = normalizeSpeed(speed);
  if (active === null) return null;
  const ActiveIcon = speedIcon(active);
  const label = prettySpeed(active);
  if (!onChange || disabled) {
    return (
      <span className={cn(chipClass(), disabled && 'opacity-60', className)} title={`Speed: ${label}`}>
        <ActiveIcon className="size-3 opacity-70" />
        <span className={labelClassName}>{label}</span>
      </span>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title={`Speed: ${label}`}
          aria-label={`Speed: ${label}. Click to change.`}
          className={cn(
            chipClass(),
            'cursor-pointer transition-colors duration-150',
            'hover:bg-gray-200 dark:hover:bg-white/10',
            'focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-300 dark:focus-visible:ring-white/20',
            className
          )}
        >
          <ActiveIcon className="size-3 opacity-70" />
          <span className={labelClassName}>{label}</span>
          <ChevronDown className="size-2.5 opacity-50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-56">
        <DropdownMenuLabel>
          <Gauge />
          Speed
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {SPEED_OPTIONS.map((option) => {
          const isCurrent = option === active;
          const OptionIcon = speedIcon(option);
          return (
            <DropdownMenuItem
              key={option}
              onSelect={() => {
                if (!isCurrent) onChange(option);
              }}
            >
              <OptionIcon />
              <span className="flex flex-1 flex-col">
                <span>{prettySpeed(option)}</span>
                <span className="text-[11px] text-gray-500 dark:text-gray-400">{speedDescription(option)}</span>
              </span>
              <Check className={cn('size-3 shrink-0', isCurrent ? 'opacity-100' : 'opacity-0')} />
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface PermissionModePillProps {
  mode?: PermissionMode | string | null;
  onChange?: (mode: PermissionMode) => void;
  disabled?: boolean;
  className?: string;
  /** See :attr:`ModelPillProps.labelClassName`. */
  labelClassName?: string;
}

/**
 * Execute/Plan picker for the agent session permission mode.
 *
 * It intentionally mirrors the effort picker: both controls are
 * session-level execution knobs, so they should read as compact
 * metadata instead of primary compose actions.
 */
export function PermissionModePill({
  mode,
  onChange,
  disabled = false,
  className,
  labelClassName,
}: PermissionModePillProps) {
  const active = normalizePermissionMode(mode);
  const ActiveIcon = permissionModeIcon(active);
  const label = prettyPermissionMode(active);
  const isPlan = active === 'plan';
  const modeClass = cn(chipClass(), isPlan && PLAN_MODE_CHIP);

  if (!onChange || disabled) {
    return (
      <span className={cn(modeClass, disabled && 'opacity-60', className)} title={`Agent mode: ${label}`}>
        <ActiveIcon className={cn('size-3 opacity-70', isPlan && PLAN_MODE_ICON)} />
        <span className={labelClassName}>{label}</span>
      </span>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title={`Agent mode: ${label}`}
          aria-label={`Agent mode: ${label}. Click to change.`}
          className={cn(
            modeClass,
            'cursor-pointer transition-colors duration-150',
            isPlan
              ? 'hover:bg-[#245f9f]/[0.11] dark:hover:bg-[#6fa3dc]/24'
              : 'hover:bg-gray-200 dark:hover:bg-white/10',
            'focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-300 dark:focus-visible:ring-white/20',
            className
          )}
        >
          <ActiveIcon className={cn('size-3 opacity-70', isPlan && PLAN_MODE_ICON)} />
          <span className={labelClassName}>{label}</span>
          <ChevronDown className="size-2.5 opacity-50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-56">
        <DropdownMenuLabel>
          <ShieldCheck />
          Agent mode
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {PERMISSION_MODES.map((nextMode) => {
          const isCurrent = nextMode === active;
          const ModeIcon = permissionModeIcon(nextMode);
          return (
            <DropdownMenuItem
              key={nextMode}
              className={cn(nextMode === 'plan' && '[&_svg]:text-[#245f9f] dark:[&_svg]:text-[#bddcff]')}
              onSelect={() => {
                if (!isCurrent) onChange(nextMode);
              }}
            >
              <ModeIcon />
              <span className="flex flex-1 flex-col">
                <span>{prettyPermissionMode(nextMode)}</span>
                <span className="text-[11px] text-gray-500 dark:text-gray-400">
                  {permissionModeDescription(nextMode)}
                </span>
              </span>
              <Check className={cn('size-3 shrink-0', isCurrent ? 'opacity-100' : 'opacity-0')} />
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// -----------------------------------------------------------------------------
// Format helpers
// -----------------------------------------------------------------------------

const TIER_LABELS: Record<string, string> = {
  opus: 'Opus',
  sonnet: 'Sonnet',
  haiku: 'Haiku',
};

/**
 * Turn a Claude model id ("claude-opus-4-8", "claude-sonnet-4-5") into a
 * humane label ("Opus 4.8", "Sonnet 4.5"). Falls back to a stripped id
 * when the format is unrecognized.
 */
function prettyModel(model: string): string {
  const stripped = model.replace(/^claude-/, '');
  // Match "<tier>-<major>-<minor>" with optional trailing tag (e.g. "opus-4-8", "opus-4-8-thinking-xhigh").
  const match = stripped.match(/^([a-z]+)-(\d+)-(\d+)/i);
  if (match) {
    const [, tier, major, minor] = match;
    const label = TIER_LABELS[tier.toLowerCase()] ?? capitalize(tier);
    return `${label} ${major}.${minor}`;
  }
  return capitalize(stripped);
}

const EFFORT_LABELS: Record<string, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  xhigh: 'Extra High',
  'extra-high': 'Extra High',
  max: 'Max',
};

/**
 * Reasoning levels the ``claude`` CLI accepts via ``--effort``.
 * Order is the user-facing ordering in the picker (cheapest → most
 * expensive). Kept in lock-step with ``EFFORT_LEVELS`` on the
 * Python side (``antioch.server.agent.frames``).
 */
const EFFORT_LEVELS: readonly string[] = ['low', 'medium', 'high', 'xhigh', 'max'] as const;

/** Map a reasoning-effort id to a human label ("xhigh" → "Extra High"). */
function prettyEffort(effort: string): string {
  return EFFORT_LABELS[effort.toLowerCase()] ?? capitalize(effort);
}

// Per-level glyphs for the picker. Battery icons carry the "more
// available budget" intuition for the bottom three rungs while
// staying visually heavier than the thin signal bars. The top two
// get distinct shapes so xhigh / max read as a step-change rather
// than "another battery". Kept as a small lookup so the picker stays
// a straight render with no inline branching.
const EFFORT_ICONS: Record<string, LucideIcon> = {
  low: BatteryLow,
  medium: BatteryMedium,
  high: BatteryFull,
  xhigh: Sparkles,
  'extra-high': Sparkles,
  max: Flame,
};

function effortIcon(effort: string): LucideIcon {
  return EFFORT_ICONS[effort.toLowerCase()] ?? Signal;
}

const PERMISSION_MODES: readonly PermissionMode[] = ['bypassPermissions', 'plan'] as const;

const SPEED_OPTIONS: readonly AgentSpeed[] = ['normal', 'fast'] as const;

function normalizeSpeed(value?: AgentSpeed | string | null): AgentSpeed | null {
  return value === 'normal' || value === 'fast' ? value : null;
}

function prettySpeed(value: AgentSpeed): string {
  return value === 'fast' ? 'Fast' : 'Normal';
}

function speedDescription(value: AgentSpeed): string {
  return value === 'fast' ? 'Beta fast mode; premium pricing' : 'Default speed and pricing';
}

function speedIcon(value: AgentSpeed): LucideIcon {
  return value === 'fast' ? Zap : Signal;
}

function normalizePermissionMode(mode?: PermissionMode | string | null): PermissionMode {
  return mode === 'plan' ? 'plan' : 'bypassPermissions';
}

function prettyPermissionMode(mode: PermissionMode): string {
  return mode === 'plan' ? 'Plan' : 'Execute';
}

function permissionModeDescription(mode: PermissionMode): string {
  return mode === 'plan'
    ? 'Read-only; the agent drafts a plan before touching anything'
    : 'Default; the agent can run tools without prompting';
}

function permissionModeIcon(mode: PermissionMode): LucideIcon {
  return mode === 'plan' ? FileText : TerminalSquare;
}

function capitalize(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}
